import React, { useRef, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Table,
  Tabs,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import useSWR from 'swr';

import {
  TeamInvitationSource,
  TeamInvitationStatus,
  UserRole,
  canPauseTeamMemberAccess,
  type AccountSetupLink,
  type CreateDriverRequest,
  type DriverListItem,
  type CreateTeamMemberRequest,
  type Team,
  type TeamInvitation,
  type UpdateTeamRequest,
  type User,
} from '@kangaroo/shared';
import { PageHeader, SectionCard, colors, headerOutlinedButtonSx } from '@kangaroo/ui';

import { SectionPanelSkeleton, TableRowsSkeleton } from '../../components/loading/PageSkeletons';

import { CollapsibleSectionCard } from '../../components/layout/CollapsibleSectionCard';
import { allDriversKey, createDriver, fetchAllDrivers } from '../../features/drivers';
import {
  createTeamMember,
  currentTeamEntitlementsKey,
  currentTeamInvitationsKey,
  currentTeamKey,
  currentTeamMembersKey,
  fetchCurrentTeam,
  fetchTeamEntitlements,
  fetchTeamMembers,
  fetchTeamInvitations,
  acceptTeamInvitation,
  resendTeamInvitation,
  updateCurrentTeam,
  updateTeamMemberAccess,
  updateTeamMemberRoles,
} from '../../features/teams';
import { canManageDrivers, canManageTeam, canViewTeamMembers } from '../../lib/roles';
import { PAGE_ICONS } from '../../lib/pageIcons';
import { viewerIsTeamWorkspaceOwner } from '../../lib/teamWorkspace';
import { useAuthStore } from '../../store/authStore';
import { AddDriverDialog } from '../drivers/AddDriverDialog';
import { DriverDetailContent } from '../drivers/DriverDetailContent';
import { DriversPage } from '../drivers/DriversPage';
import { TeamInfoDialog } from './TeamInfoDialog';
import { FamilyTeamJoinCard } from './FamilyTeamJoinCard';

type TeamRole = 'Team Owner' | 'Head Coach' | 'Mechanic' | 'Parent' | 'Driver';
type RoleFilter = 'All Roles' | TeamRole;
type MemberRosterView = 'all' | 'active' | 'archived';
type TeamRosterTab = 'staff' | 'drivers' | 'coaches' | 'mechanics' | 'parents' | 'archived';

interface TeamMemberRow {
  id: string;
  name: string;
  email: string;
  initials: string;
  role: TeamRole;
  userRole: UserRole;
  team: string;
  contact: string;
  joined: string;
  lastActive: string;
  status: 'Active' | 'Archived' | 'Pending';
  accountDisabled: boolean;
  roles: TeamRole[];
  userRoles: UserRole[];
  driverId?: string;
}

const ROLE_STYLES: Record<TeamRole, { color: string; bg: string }> = {
  'Team Owner': { color: '#A78BFA', bg: 'rgba(167,139,250,0.16)' },
  'Head Coach': { color: '#60A5FA', bg: 'rgba(96,165,250,0.16)' },
  Mechanic: { color: '#F59E0B', bg: 'rgba(245,158,11,0.16)' },
  Parent: { color: '#F87171', bg: 'rgba(248,113,113,0.16)' },
  Driver: { color: '#4ADE80', bg: 'rgba(74,222,128,0.16)' },
};

const ROLE_STYLES_LIGHT: Record<TeamRole, { color: string; bg: string }> = {
  'Team Owner': { color: '#6D28D9', bg: 'rgba(124,58,237,0.12)' },
  'Head Coach': { color: '#1D4ED8', bg: 'rgba(59,130,246,0.12)' },
  Mechanic: { color: '#B45309', bg: 'rgba(245,158,11,0.12)' },
  Parent: { color: '#B91C1C', bg: 'rgba(239,68,68,0.12)' },
  Driver: { color: '#15803D', bg: 'rgba(34,197,94,0.12)' },
};

const MEMBER_GROUP_OPTIONS = ['Management', 'Coaching', 'Technical', 'Junior Drivers', 'Parents'] as const;

const EDITABLE_MEMBER_ROLE_OPTIONS = [
  { role: UserRole.PARENT, label: 'Parent' },
  { role: UserRole.COACH, label: 'Head Coach' },
  { role: UserRole.MECHANIC, label: 'Mechanic' },
] as const;

const EMPTY_TEAM_PROFILE: Team = {
  id: 'current-team',
  name: 'Current Team',
  slug: 'current-team',
  code: 'TEAM',
  logoUrl: null,
  primaryColor: '#2563EB',
  secondaryColor: '#16A34A',
  phone: 'Not set',
  email: 'Not set',
  website: null,
  contactName: 'Not set',
  contactEmail: 'Not set',
  contactPhone: 'Not set',
  primaryLocation: 'Not set',
  addressLine1: 'Not set',
  addressLine2: null,
  city: 'Not set',
  state: 'Not set',
  country: 'Not set',
  postalCode: 'Not set',
  timezone: 'UTC',
  createdAt: '2026-01-01T00:00:00.000Z',
  updatedAt: '2026-01-01T00:00:00.000Z',
};

const rosterQuery = { status: 'ALL' as const };

function getErrorMessage(error: unknown, fallback = 'Unable to save changes') {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) {
      return response.data.message;
    }
  }

  return error instanceof Error ? error.message : fallback;
}

function formatInviteAge(dateString: string) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Invited recently';

  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.max(0, Math.floor(diffMs / 86_400_000));
  if (diffDays === 0) return 'Invited today';
  if (diffDays === 1) return 'Invited 1 day ago';
  return `Invited ${diffDays} days ago`;
}

function invitationRoleLabel(role: TeamInvitation['role']): TeamRole {
  if (role === UserRole.TEAM_OWNER) return 'Team Owner';
  if (role === UserRole.COACH) return 'Head Coach';
  if (role === UserRole.MECHANIC) return 'Mechanic';
  if (role === UserRole.PARENT) return 'Parent';
  return 'Driver';
}

function groupForRole(role: UserRole) {
  if (role === UserRole.COACH) return 'Coaching';
  if (role === UserRole.MECHANIC) return 'Technical';
  if (role === UserRole.PARENT) return 'Parents';
  if (role === UserRole.DRIVER) return 'Junior Drivers';
  return 'Management';
}

function uniqueUserRoles(user: User) {
  return Array.from(new Set([user.role, ...(user.additionalRoles ?? [])]));
}

function hasUserRole(userRoles: UserRole[], role: UserRole) {
  return userRoles.includes(role);
}

function canEditMemberRoles(member: TeamMemberRow) {
  return !hasUserRole(member.userRoles, UserRole.TEAM_OWNER) && !hasUserRole(member.userRoles, UserRole.DRIVER);
}

function teamAcronym(team: Team) {
  const codeLetters = team.code.replace(/[^a-z]/gi, '').slice(0, 3).toUpperCase();
  return codeLetters || initialsFromName(team.name) || 'TM';
}

function isUnsetValue(value: string | null | undefined) {
  return !value || value.trim().toLowerCase() === 'not set';
}

function displayTeamField(value: string | null | undefined) {
  return isUnsetValue(value) ? 'Add in settings' : value;
}

function initialsFromName(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function formatJoined(dateString: string | null | undefined) {
  if (!dateString) return '-';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

function formatLastActive(dateString: string | null | undefined) {
  const formatted = formatJoined(dateString);
  return formatted === '-' ? 'Never' : formatted;
}

function roleChip(role: TeamRole, isDark: boolean) {
  const style = (isDark ? ROLE_STYLES : ROLE_STYLES_LIGHT)[role];

  return (
    <Chip
      label={role}
      size="small"
      sx={{
        height: 22,
        fontSize: '0.68rem',
        fontWeight: 700,
        color: style.color,
        bgcolor: style.bg,
        '& .MuiChip-label': { px: 0.9 },
      }}
    />
  );
}

function memberRoleLabel(role: UserRole): TeamRole {
  if (role === UserRole.TEAM_OWNER) return 'Team Owner';
  if (role === UserRole.COACH) return 'Head Coach';
  if (role === UserRole.MECHANIC) return 'Mechanic';
  if (role === UserRole.PARENT) return 'Parent';
  return 'Driver';
}

function buildUserMembers(users: User[]): TeamMemberRow[] {
  return users.map((user) => {
    const userRoles = uniqueUserRoles(user);
    const roles = userRoles.map(memberRoleLabel);

    return {
      id: user.id,
      name: user.name,
      email: user.email,
      initials: initialsFromName(user.name) || user.email[0]?.toUpperCase() || 'U',
      role: memberRoleLabel(user.role),
      userRole: user.role,
      team: groupForRole(user.role),
      contact: user.email,
      joined: formatJoined(user.createdAt),
      lastActive: formatLastActive(user.lastActivityAt ?? user.lastLoginAt),
      status: user.accountDisabled ? 'Archived' : 'Active',
      accountDisabled: user.accountDisabled === true,
      roles,
      userRoles,
    };
  });
}

function buildDriverMembers(drivers: DriverListItem[], existingUserIds: Set<string>): TeamMemberRow[] {
  return drivers
    .filter((driver) => driver.userId && !existingUserIds.has(driver.userId))
    .map((driver) => ({
      id: `driver-${driver.id}`,
      name: driver.name,
      email: driver.email,
      initials: driver.initials || initialsFromName(driver.name) || 'D',
      role: 'Driver' as const,
      userRole: UserRole.DRIVER,
      team: 'Junior Drivers',
      contact: driver.email,
      joined: formatJoined(driver.createdAt),
      lastActive: '-',
      status: 'Active' as const,
      accountDisabled: false,
      roles: ['Driver' as const],
      userRoles: [UserRole.DRIVER],
      driverId: driver.id,
    }));
}

function memberStatusChip(status: TeamMemberRow['status']) {
  if (status === 'Archived') {
    return (
      <Chip
        label="Archived"
        size="small"
        sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#FBBF24', bgcolor: 'rgba(245,158,11,0.14)' }}
      />
    );
  }

  return (
    <Chip
      label={status}
      size="small"
      sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#4ADE80', bgcolor: 'rgba(34,197,94,0.14)' }}
    />
  );
}

function handleKeyboardActivation(event: React.KeyboardEvent, action: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  event.preventDefault();
  action();
}

export function TeamManagementPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const user = useAuthStore((state) => state.user);
  const {
    data: teamProfile,
    error: teamProfileError,
    isLoading: teamProfileLoading,
    mutate: mutateTeamProfile,
  } = useSWR(currentTeamKey, fetchCurrentTeam);
  const isWorkspaceOwner = viewerIsTeamWorkspaceOwner(user, teamProfile);
  const isFamilyTeam = Boolean(teamProfile?.isFamilyTeamWorkspace);
  const canManageTeamAccounts = user ? canManageTeam(user.role, { isTeamWorkspaceOwner: isWorkspaceOwner }) : false;
  const canAddDrivers = user ? canManageDrivers(user.role, { isTeamWorkspaceOwner: isWorkspaceOwner }) : false;
  const canSeeTeamMembers = user ? canViewTeamMembers(user.role, { isTeamWorkspaceOwner: isWorkspaceOwner }) : false;
  const showFamilyJoin = Boolean(teamProfile?.isFamilyTeamWorkspace) && canManageTeamAccounts;
  const driversSectionRef = useRef<HTMLDivElement>(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('All Roles');
  const [memberRosterView, setMemberRosterView] = useState<MemberRosterView>('all');
  const [rosterTab, setRosterTab] = useState<TeamRosterTab>('staff');
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [teamInfoOpen, setTeamInfoOpen] = useState(false);
  const [teamInfoSaving, setTeamInfoSaving] = useState(false);
  const [teamInfoError, setTeamInfoError] = useState<string | null>(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [addDriverDrawerOpen, setAddDriverDrawerOpen] = useState(false);
  const [listDrawerOpen, setListDrawerOpen] = useState(false);
  const [driverDrawerOpen, setDriverDrawerOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [setupLinks, setSetupLinks] = useState<AccountSetupLink[]>([]);
  const [memberAccessUpdatingId, setMemberAccessUpdatingId] = useState<string | null>(null);
  const [rolesDialogMember, setRolesDialogMember] = useState<TeamMemberRow | null>(null);
  const [memberRoleSelections, setMemberRoleSelections] = useState<UserRole[]>([]);
  const [memberRolesSaving, setMemberRolesSaving] = useState(false);
  const [memberRolesError, setMemberRolesError] = useState<string | null>(null);

  const {
    data: driverRoster,
    error: driverRosterError,
    isLoading: driverRosterLoading,
    mutate: mutateDriverRoster,
  } = useSWR(allDriversKey(rosterQuery, showFamilyJoin), () => fetchAllDrivers(rosterQuery, showFamilyJoin));

  const {
    data: invitations = [],
    error: invitationsError,
    isLoading: invitationsLoading,
    mutate: mutateInvitations,
  } = useSWR(canManageTeamAccounts ? currentTeamInvitationsKey : null, fetchTeamInvitations);

  const {
    data: teamUsers = [],
    error: teamMembersError,
    isLoading: teamMembersLoading,
    mutate: mutateTeamMembers,
  } = useSWR(canSeeTeamMembers ? currentTeamMembersKey : null, fetchTeamMembers);

  const { mutate: mutateEntitlements } = useSWR(
    canManageTeamAccounts ? currentTeamEntitlementsKey : null,
    fetchTeamEntitlements,
  );
  const drivers = driverRoster?.drivers ?? [];
  const assignedKartLabelByDriverId = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const assignment of driverRoster?.kartAssignments ?? []) {
      labels[assignment.driverId] = assignment.kartNumber ? `#${assignment.kartNumber}` : (assignment.nickname ?? 'Vehicle');
    }
    return labels;
  }, [driverRoster?.kartAssignments]);
  const displayedTeam = teamProfile ?? EMPTY_TEAM_PROFILE;
  const isTeamProfileLoading = teamProfileLoading && !teamProfile;
  const teamMembers = useMemo(() => buildUserMembers(teamUsers), [teamUsers]);
  const driverMemberRows = useMemo(
    () => buildDriverMembers(drivers, new Set(teamUsers.map((teamUser) => teamUser.id))),
    [drivers, teamUsers],
  );

  const totalDrivers = drivers.length;
  const totalMembers = teamMembers.length;
  const coaches = teamMembers.filter((member) => member.roles.includes('Head Coach') && !member.accountDisabled).length;
  const mechanics = teamMembers.filter((member) => member.roles.includes('Mechanic') && !member.accountDisabled).length;
  const parents = teamMembers.filter((member) => member.roles.includes('Parent') && !member.accountDisabled).length;
  const archivedStaffCount = teamMembers.filter((member) => member.userRole !== UserRole.DRIVER && member.accountDisabled).length;
  const staffMembers = teamMembers.filter((member) => member.userRole !== UserRole.DRIVER);
  const familyMembers = isFamilyTeam ? [...staffMembers, ...driverMemberRows] : staffMembers;
  const memberGroups = MEMBER_GROUP_OPTIONS.map((groupName) => ({
    name: groupName,
    members: teamMembers.filter((member) => member.team === groupName || member.userRoles.some((role) => groupForRole(role) === groupName)).length,
    lead: teamMembers.find((member) => member.team === groupName)?.name ?? 'Unassigned',
  })).filter((group) => group.members > 0);
  const pendingInvitations = invitations.filter((invitation) => invitation.status === TeamInvitationStatus.PENDING);

  const memberRowsForCurrentTab = isFamilyTeam && rosterTab === 'staff' ? familyMembers : staffMembers;
  const filteredStaffMembers = memberRowsForCurrentTab.filter((member) => {
    if (memberRosterView === 'active' && member.accountDisabled) return false;
    if (memberRosterView === 'archived' && !member.accountDisabled) return false;
    if (memberSearch && !`${member.name} ${member.email}`.toLowerCase().includes(memberSearch.toLowerCase())) return false;
    if (roleFilter !== 'All Roles' && !member.roles.includes(roleFilter)) return false;
    return true;
  });

  function applyRosterTab(tab: TeamRosterTab) {
    setRosterTab(tab);
    setMemberSearch('');

    switch (tab) {
      case 'staff':
        setRoleFilter('All Roles');
        setMemberRosterView('all');
        break;
      case 'drivers':
        break;
      case 'coaches':
        setRoleFilter('Head Coach');
        setMemberRosterView('all');
        break;
      case 'mechanics':
        setRoleFilter('Mechanic');
        setMemberRosterView('all');
        break;
      case 'parents':
        setRoleFilter('Parent');
        setMemberRosterView('all');
        break;
      case 'archived':
        setRoleFilter('All Roles');
        setMemberRosterView('archived');
        break;
    }
  }

  function openInvitationsDrawer() {
    setListDrawerOpen(true);
  }

  function openDriverDrawer(driverId: string) {
    setSelectedDriverId(driverId);
    setDriverDrawerOpen(true);
  }

  function closeDriverDrawer() {
    setDriverDrawerOpen(false);
    setSelectedDriverId(null);
  }

  function closeListDrawer() {
    setListDrawerOpen(false);
  }

  function openTeamInfoDialog() {
    if (!canManageTeamAccounts) return;
    setTeamInfoError(null);
    setTeamInfoOpen(true);
  }

  function openAccountDialog() {
    if (!canManageTeamAccounts) return;
    setAccountDialogOpen(true);
  }

  async function handleSaveTeamInfo(payload: UpdateTeamRequest) {
    setTeamInfoSaving(true);
    setTeamInfoError(null);

    try {
      const updatedTeam = await updateCurrentTeam(payload);
      await mutateTeamProfile(updatedTeam, { revalidate: false });
      setTeamInfoOpen(false);
      setSnackbarMessage('Team info updated');
    } catch (error) {
      setTeamInfoError(getErrorMessage(error));
    } finally {
      setTeamInfoSaving(false);
    }
  }

  async function handleCreateDriverAccount(payload: CreateDriverRequest) {
    const created = await createDriver(payload);
    await mutateDriverRoster();
    await mutateEntitlements();
    setSetupLinks(created.accountSetupLinks);
    setSnackbarMessage(
      created.accountSetupLinks.length > 0
        ? `${created.driver.name} was added. Send the setup link before it expires.`
        : `${created.driver.name} was added.`,
    );
  }

  async function handleCreateTeamAccount(payload: CreateTeamMemberRequest) {
    const created = await createTeamMember(payload);
    setSetupLinks(created.accountSetupLinks);
    await mutateTeamMembers(
      (current = []) => [created.user, ...current.filter((user) => user.email.toLowerCase() !== created.user.email.toLowerCase())],
      { revalidate: false },
    );
    await mutateEntitlements();
    setSnackbarMessage(
      created.accountSetupLinks.length > 0
        ? `${created.user.name} was created. Send the setup link before it expires.`
        : `${created.user.name} was created.`,
    );
  }

  async function copySetupLink(link: AccountSetupLink) {
    await navigator.clipboard.writeText(link.registrationLink);
    setSnackbarMessage(`Setup link copied for ${link.email}.`);
  }

  async function handleResendInvitation(invitation: TeamInvitation) {
    try {
      await resendTeamInvitation(invitation.id);
      await mutateInvitations();
      setSnackbarMessage(`Invitation resent to ${invitation.name}`);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error, 'Unable to resend invitation'));
    }
  }

  async function handleAcceptInvitation(invitation: TeamInvitation) {
    try {
      await acceptTeamInvitation(invitation.id);
      await Promise.all([mutateInvitations(), mutateTeamMembers(), mutateDriverRoster()]);
      setSnackbarMessage(`${invitation.name} was added to the team.`);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error, 'Unable to accept request'));
    }
  }

  async function handleMemberAccessChange(member: TeamMemberRow, disabled: boolean) {
    setMemberAccessUpdatingId(member.id);
    try {
      await updateTeamMemberAccess(member.id, disabled);
      await Promise.all([
        mutateTeamMembers(),
        canManageTeamAccounts ? mutateEntitlements() : Promise.resolve(),
      ]);
      setSnackbarMessage(disabled ? `${member.name} was archived.` : `${member.name} was restored.`);
      if (disabled) {
        applyRosterTab('archived');
      } else if (rosterTab === 'archived') {
        applyRosterTab('staff');
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error, 'Unable to update member access'));
    } finally {
      setMemberAccessUpdatingId(null);
    }
  }

  function openMemberRolesDialog(member: TeamMemberRow) {
    setRolesDialogMember(member);
    setMemberRoleSelections(
      EDITABLE_MEMBER_ROLE_OPTIONS
        .map((option) => option.role)
        .filter((role) => hasUserRole(member.userRoles, role)),
    );
    setMemberRolesError(null);
  }

  function closeMemberRolesDialog() {
    if (memberRolesSaving) return;
    setRolesDialogMember(null);
    setMemberRoleSelections([]);
    setMemberRolesError(null);
  }

  function toggleMemberRole(role: UserRole, checked: boolean) {
    setMemberRolesError(null);
    setMemberRoleSelections((current) => {
      if (checked) {
        return Array.from(new Set([...current, role]));
      }

      return current.filter((selectedRole) => selectedRole !== role);
    });
  }

  async function handleSaveMemberRoles() {
    if (!rolesDialogMember) return;
    if (memberRoleSelections.length === 0) {
      setMemberRolesError('Choose at least one role.');
      return;
    }

    const primaryRole = memberRoleSelections.includes(UserRole.PARENT)
      ? UserRole.PARENT
      : memberRoleSelections[0]!;
    const additionalRoles = memberRoleSelections.filter((role) => role !== primaryRole);

    setMemberRolesSaving(true);
    setMemberRolesError(null);
    try {
      const updated = await updateTeamMemberRoles(rolesDialogMember.id, { role: primaryRole, additionalRoles });
      await mutateTeamMembers(
        (current = []) => current.map((member) => (member.id === updated.id ? updated : member)),
        { revalidate: false },
      );
      await mutateEntitlements();
      setSnackbarMessage(`${updated.name}'s roles were updated.`);
      setRolesDialogMember(null);
      setMemberRoleSelections([]);
    } catch (error) {
      setMemberRolesError(getErrorMessage(error, 'Unable to update roles'));
    } finally {
      setMemberRolesSaving(false);
    }
  }

  const rosterCountValue = (value: number) => (driverRosterLoading ? '...' : driverRosterError ? '—' : value);
  const memberCountValue = (value: number) => (teamMembersLoading ? '...' : value);
  const familyCountValue = (value: number) => teamMembersLoading ? '...' : rosterCountValue(value);
  const activeStaffCount = staffMembers.filter((member) => !member.accountDisabled).length;
  const showStaffTable = canSeeTeamMembers && rosterTab !== 'drivers';
  const showDriversTable = rosterTab === 'drivers';

  const rosterTabs: Array<{ id: TeamRosterTab; label: string; count: number | string }> = isFamilyTeam
    ? [
        { id: 'staff', label: 'Family', count: familyCountValue(parents + totalDrivers) },
        { id: 'drivers', label: 'Drivers', count: rosterCountValue(totalDrivers) },
        { id: 'parents', label: 'Parents', count: memberCountValue(parents) },
        ...(canManageTeamAccounts ? [{ id: 'archived' as const, label: 'Archived', count: memberCountValue(archivedStaffCount) }] : []),
      ]
    : [
        { id: 'staff', label: 'Staff', count: memberCountValue(activeStaffCount) },
        { id: 'drivers', label: 'Drivers', count: rosterCountValue(totalDrivers) },
        { id: 'coaches', label: 'Coaches', count: memberCountValue(coaches) },
        { id: 'mechanics', label: 'Mechanics', count: memberCountValue(mechanics) },
        { id: 'parents', label: 'Parents', count: memberCountValue(parents) },
        ...(canManageTeamAccounts ? [{ id: 'archived' as const, label: 'Archived', count: memberCountValue(archivedStaffCount) }] : []),
      ];

  const quickActions = [
    ...(canManageTeamAccounts ? [
      { label: 'Create Account', onClick: openAccountDialog },
      { label: 'Team Settings', onClick: openTeamInfoDialog },
    ] : []),
    ...(canAddDrivers ? [
      { label: 'Add Driver', onClick: () => setAddDriverDrawerOpen(true) },
    ] : []),
  ];

  function renderRosterTabs() {
    return (
      <Tabs
        value={rosterTab}
        onChange={(_event, value: TeamRosterTab) => applyRosterTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 40,
          mb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 40,
            px: 1.5,
            py: 0.75,
            fontSize: '0.78rem',
            fontWeight: 700,
            textTransform: 'none',
          },
        }}
      >
        {rosterTabs.map((tab) => (
          <Tab
            key={tab.id}
            value={tab.id}
            label={`${tab.label} (${tab.count})`}
          />
        ))}
      </Tabs>
    );
  }

  function renderOverview() {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) 300px' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        <Box sx={{ display: 'grid', gap: 2.5, minWidth: 0 }}>
          {renderRosterTabs()}

          {showStaffTable ? renderStaffAccessTable() : null}

          {showDriversTable ? (
            <Box ref={driversSectionRef}>
              <DriversPage
                embedded
                onSelectDriver={openDriverDrawer}
                onAddDriver={() => setAddDriverDrawerOpen(true)}
              />
            </Box>
          ) : null}
        </Box>

        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Box
            onClick={canManageTeamAccounts ? openTeamInfoDialog : undefined}
            onKeyDown={canManageTeamAccounts ? (event) => handleKeyboardActivation(event, openTeamInfoDialog) : undefined}
            role={canManageTeamAccounts ? 'button' : undefined}
            tabIndex={canManageTeamAccounts ? 0 : undefined}
            sx={canManageTeamAccounts ? {
              cursor: 'pointer',
              borderRadius: 2,
              transition: 'border-color 0.15s ease',
              '&:focus-visible': { outline: `2px solid ${colors.primary[400]}`, outlineOffset: 3 },
              '&:hover .MuiCard-root': { borderColor: 'rgba(96,165,250,0.35)' },
            } : undefined}
          >
            <SectionCard
              contentSx={{ p: 2.5, ...(canManageTeamAccounts ? { cursor: 'pointer' } : {}) }}
              sx={canManageTeamAccounts ? { transition: 'border-color 0.15s ease' } : undefined}
            >
              {isTeamProfileLoading ? (
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Skeleton variant="rounded" width={62} height={62} sx={{ borderRadius: 2 }} />
                    <Box sx={{ flex: 1, pt: 0.4 }}>
                      <Skeleton variant="text" width="72%" height={20} />
                      <Skeleton variant="text" width="42%" height={16} />
                      <Skeleton variant="text" width="34%" height={18} />
                    </Box>
                  </Box>
                  <Stack spacing={1.1}>
                    {[0, 1, 2, 3, 4].map((item) => (
                      <Box key={item} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Skeleton variant="text" width={92} height={18} />
                        <Skeleton variant="text" width={item === 2 ? 150 : 72} height={18} />
                      </Box>
                    ))}
                  </Stack>
                  <Skeleton variant="rounded" height={34} />
                </Stack>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 62,
                        height: 62,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: 'subtleFill.light',
                        border: '1px solid',
                        borderColor: 'divider',
                        fontStyle: 'italic',
                        fontWeight: 800,
                      }}
                    >
                      {teamAcronym(displayedTeam)}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700}>{displayedTeam.name}</Typography>
                      <Typography variant="caption" color="text.secondary">Team Code</Typography>
                      <Typography variant="body2" fontSize="0.78rem">{displayedTeam.code}</Typography>
                    </Box>
                  </Box>

                  {teamProfileError && (
                    <Typography sx={{ mb: 1.25, fontSize: '0.72rem', color: 'error.light' }}>
                      {getErrorMessage(teamProfileError, 'Team profile could not be loaded.')}
                    </Typography>
                  )}

                  <Stack spacing={1.1} sx={{ mb: 2.25 }}>
                    {[
                      ['Primary Location', displayTeamField(displayedTeam.primaryLocation)],
                      ['Phone', displayTeamField(displayedTeam.phone)],
                      ['Email', displayTeamField(displayedTeam.email)],
                      ['Groups', String(memberGroups.length)],
                      ['Members', teamMembersLoading ? '...' : String(totalMembers)],
                    ].map(([label, value]) => (
                      <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight={600} fontSize="0.8rem">{value}</Typography>
                      </Box>
                    ))}
                  </Stack>

                  {canManageTeamAccounts && (
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      startIcon={<TuneIcon />}
                      onClick={(event) => {
                        event.stopPropagation();
                        openTeamInfoDialog();
                      }}
                    >
                      Edit Team Info
                    </Button>
                  )}
                </>
              )}
            </SectionCard>
          </Box>

          {renderRightRail()}
        </Stack>
      </Box>
    );
  }

  function renderStaffAccessTable() {
    const tableColumns = canManageTeamAccounts
      ? ['Member', 'Role', 'Group', 'Status', 'Last active', 'Actions']
      : ['Member', 'Role', 'Group', 'Status', 'Last active'];
    const sectionTitle = rosterTab === 'coaches'
      ? 'Coaches'
      : rosterTab === 'mechanics'
        ? 'Mechanics'
        : rosterTab === 'parents'
          ? (isFamilyTeam ? 'Parents' : 'Parents / Guardians')
          : rosterTab === 'archived'
            ? 'Archived Staff'
            : (isFamilyTeam ? 'Family Access' : 'Staff & Access');
        const isFamilyAccessTab = isFamilyTeam && rosterTab === 'staff';

    if (isFamilyAccessTab && (driverRosterLoading || driverRosterError)) {
      return (
        <SectionCard title="Family Access" subtitle="Parents, guardians, and drivers on this workspace.">
          <Alert severity={driverRosterError ? 'error' : 'info'}>
            {driverRosterError
              ? 'The complete family roster is unavailable. Refresh before reviewing members or changing access.'
              : 'Loading the complete family roster…'}
          </Alert>
        </SectionCard>
      );
    }

    return (
      <SectionCard
        title={sectionTitle}
        subtitle={rosterTab === 'archived'
          ? 'Archived accounts cannot sign in until restored.'
          : isFamilyTeam
            ? isFamilyAccessTab
              ? 'Parents, guardians, and drivers on this workspace.'
              : 'Parents and guardians on this workspace.'
            : 'Manage staff access here. Use the Drivers tab for the driver roster.'}
        action={
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {rosterTab !== 'archived' ? (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={memberRosterView}
                onChange={(_event, nextView: MemberRosterView | null) => {
                  if (nextView) setMemberRosterView(nextView);
                }}
                sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.45, fontSize: '0.72rem', fontWeight: 700, textTransform: 'none' } }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="active">Active</ToggleButton>
              </ToggleButtonGroup>
            ) : null}
            <TextField
              size="small"
              placeholder={isFamilyAccessTab ? 'Search family...' : 'Search staff...'}
              value={memberSearch}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setMemberSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 180 }}
            />
          </Stack>
        }
        contentSx={{ p: 0, '&:last-child': { pb: 0 } }}
      >
        {teamMembersError && (
          <Typography sx={{ px: 3, py: 2, fontSize: '0.78rem', color: 'error.light' }}>
            Staff roster could not be loaded.
          </Typography>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {tableColumns.map((column) => (
                  <TableCell key={column} sx={column === 'Actions' ? { width: 140 } : undefined}>{column}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {teamMembersLoading ? (
                <TableRowsSkeleton rows={4} columns={tableColumns.length} avatarColumn />
              ) : filteredStaffMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableColumns.length}>
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight={700}>
                        {memberRosterView === 'archived'
                          ? 'No archived staff'
                          : memberRosterView === 'active'
                            ? `No active ${isFamilyAccessTab ? 'family members' : 'staff'}`
                            : `No ${isFamilyAccessTab ? 'family members' : 'staff'} found`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {memberRosterView === 'archived'
                          ? 'Archived accounts appear here after you archive someone from the active list.'
                          : isFamilyAccessTab
                            ? 'Add a driver or clear filters to see family members.'
                            : 'Create an account or clear filters to see staff members.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredStaffMembers.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: '0.76rem' }}>{member.initials}</Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} fontSize="0.82rem" noWrap>{member.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{member.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                      {member.roles.map((role) => (
                        <Box key={role}>{roleChip(role, isDark)}</Box>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell><Typography variant="body2" fontSize="0.8rem">{member.team}</Typography></TableCell>
                  <TableCell>{memberStatusChip(member.status)}</TableCell>
                  <TableCell><Typography variant="body2" fontSize="0.8rem">{member.lastActive}</Typography></TableCell>
                  {canManageTeamAccounts ? (
                    <TableCell sx={{ width: 140, whiteSpace: 'nowrap' }}>
                      {member.driverId ? (
                        <Button
                          size="small"
                          variant="text"
                          color="primary"
                          onClick={() => openDriverDrawer(member.driverId!)}
                          sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.2, textTransform: 'none' }}
                        >
                          Details
                        </Button>
                      ) : member.id === user?.id ? (
                        <Typography variant="caption" color="text.secondary">You</Typography>
                      ) : (
                        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                          {canEditMemberRoles(member) ? (
                            <Button
                              size="small"
                              variant="text"
                              color="primary"
                              onClick={() => openMemberRolesDialog(member)}
                              sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.2, textTransform: 'none' }}
                            >
                              Roles
                            </Button>
                          ) : null}
                          {canPauseTeamMemberAccess(member.userRole) ? (
                            <Button
                              size="small"
                              variant="text"
                              color={member.accountDisabled ? 'primary' : 'inherit'}
                              disabled={memberAccessUpdatingId === member.id}
                              onClick={() => void handleMemberAccessChange(member, !member.accountDisabled)}
                              sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.2, textTransform: 'none' }}
                            >
                              {memberAccessUpdatingId === member.id
                                ? '…'
                                : member.accountDisabled
                                  ? 'Restore'
                                  : 'Archive'}
                            </Button>
                          ) : null}
                        </Stack>
                      )}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ px: 3, py: 1.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredStaffMembers.length} of{' '}
            {memberRosterView === 'archived'
              ? archivedStaffCount
              : memberRosterView === 'active'
                ? memberRowsForCurrentTab.filter((member) => !member.accountDisabled).length
                : memberRowsForCurrentTab.length}{' '}
            {memberRosterView !== 'all' ? `${memberRosterView} ` : ''}{isFamilyAccessTab ? 'family members' : 'staff'}
          </Typography>
        </Box>
      </SectionCard>
    );
  }

  function renderInvitations() {
    return (
      <>
        {invitationsError && (
          <Typography sx={{ px: 2.5, py: 1.75, fontSize: '0.78rem', color: 'error.main' }}>
            Invitations could not be loaded.
          </Typography>
        )}
        {!invitationsError && pendingInvitations.length === 0 && (
          <Box sx={{ px: 2.5, py: 3 }}>
            <Typography variant="body2" color="text.secondary">No pending invitations.</Typography>
          </Box>
        )}
        {pendingInvitations.map((invite, index) => (
          <React.Fragment key={invite.id}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 1.25, md: 2 },
                px: 2.5,
                py: 1.8,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: '0.76rem',
                    bgcolor: isDark ? undefined : 'rgba(124,58,237,0.12)',
                    color: isDark ? undefined : colors.primary[700],
                  }}
                >
                  {initialsFromName(invite.name) || invite.name[0]}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>{invite.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>{invite.email}</Typography>
                  {invite.requestedDrivers && invite.requestedDrivers.length > 0 ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      Drivers: {invite.requestedDrivers.map((driver) => (
                        driver.bringsOwnKart ? `${driver.name} (own kart)` : driver.name
                      )).join(', ')}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  rowGap: 0.75,
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                }}
              >
                {roleChip(invitationRoleLabel(invite.role), isDark)}
                {invite.source === TeamInvitationSource.REQUEST && (
                  <Chip label="Join Request" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }} />
                )}
                {invite.isMinor && (
                  <Chip label="Parent Required" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {formatInviteAge(invite.invitedAt)}
                </Typography>
                {invite.source === TeamInvitationSource.REQUEST ? (
                  <Button size="small" variant="contained" onClick={() => handleAcceptInvitation(invite)} sx={{ minWidth: 72 }}>
                    Accept
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleResendInvitation(invite)}
                    sx={{
                      ...headerOutlinedButtonSx(isDark),
                      minWidth: 72,
                      height: 28,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      textTransform: 'none',
                    }}
                  >
                    Resend
                  </Button>
                )}
              </Stack>
            </Box>
            {index < pendingInvitations.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </>
    );
  }

  function renderRightRail() {
    return (
      <Stack spacing={2} sx={{ height: '100%' }}>
        {canManageTeamAccounts ? (
        <CollapsibleSectionCard
          title="Quick Actions"
          defaultExpanded={false}
          contentSx={{ px: 0, pb: 0 }}
        >
          {quickActions.length === 0 ? (
            <Typography sx={{ px: 2.5, py: 1.5, fontSize: '0.78rem', color: 'text.secondary' }}>
              No management actions available for this role.
            </Typography>
          ) : quickActions.map((action, index) => (
            <React.Fragment key={action.label}>
              <Box
                component="div"
                role="button"
                tabIndex={0}
                onClick={action.onClick}
                onKeyDown={(event) => handleKeyboardActivation(event, action.onClick)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 1.5,
                  '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.04)' },
                  '&:focus-visible': { outline: `2px solid ${colors.primary[400]}`, outlineOffset: -2 },
                }}
              >
                <Typography component="span" variant="body2" fontWeight={500} fontSize="0.82rem">{action.label}</Typography>
                <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Box>
              {index < quickActions.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </CollapsibleSectionCard>
        ) : null}

        {isFamilyTeam && canManageTeamAccounts ? (
          <CollapsibleSectionCard
            title="Join a club team"
            subtitle="Register selected drivers with another team"
            defaultExpanded={false}
            contentSx={{ px: 0, pb: 0 }}
          >
            {driverRosterLoading || driverRosterError ? (
              <Alert severity={driverRosterError ? 'error' : 'info'} sx={{ m: 2 }}>
                {driverRosterError
                  ? 'The complete driver roster could not be loaded. Refresh before assigning drivers to another team.'
                  : 'Loading the complete driver roster before join options become available…'}
              </Alert>
            ) : (
              <FamilyTeamJoinCard
                linkedDrivers={drivers}
                assignedKartLabelByDriverId={assignedKartLabelByDriverId}
                onRequested={(teamName) => setSnackbarMessage(`Join request sent to ${teamName}.`)}
              />
            )}
          </CollapsibleSectionCard>
        ) : null}

        <CollapsibleSectionCard
          title="Upcoming Invitations"
          defaultExpanded={false}
          contentSx={{ pt: 0 }}
        >
          <Stack spacing={1.75} sx={{ flex: 1 }}>
            {invitationsLoading ? (
              <>
                <SectionPanelSkeleton height={52} />
                <SectionPanelSkeleton height={52} />
                <SectionPanelSkeleton height={52} />
              </>
            ) : pendingInvitations.slice(0, 3).map((invite) => (
              <Box key={invite.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.72rem' }}>{initialsFromName(invite.name) || invite.name[0]}</Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} fontSize="0.8rem" noWrap>{invite.name}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{invite.source === TeamInvitationSource.REQUEST ? 'Join request' : invitationRoleLabel(invite.role)}</Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{formatInviteAge(invite.invitedAt)}</Typography>
                  {invite.source === TeamInvitationSource.REQUEST ? (
                    <Button size="small" sx={{ minWidth: 0, p: 0, fontSize: '0.68rem', fontWeight: 700 }} onClick={() => handleAcceptInvitation(invite)}>
                      Accept
                    </Button>
                  ) : (
                    <Button size="small" sx={{ minWidth: 0, p: 0, fontSize: '0.68rem', fontWeight: 700 }} onClick={() => handleResendInvitation(invite)}>
                      Resend
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
            {!invitationsLoading && pendingInvitations.length === 0 && (
              <Typography variant="body2" color="text.secondary" fontSize="0.78rem">No pending invitations.</Typography>
            )}
          </Stack>

          <Button variant="text" size="small" sx={{ mt: 1.5, px: 0, alignSelf: 'flex-start', fontWeight: 700 }} onClick={openInvitationsDrawer}>
            View all invitations -&gt;
          </Button>
        </CollapsibleSectionCard>
      </Stack>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Team Management"
        subtitle="Manage your team members and account access."
        icon={PAGE_ICONS.team}
        mb={3}
        actions={
          canManageTeamAccounts ? (
            <Stack direction="row" spacing={1}>
              <Tooltip title="Invite staff, parents, or drivers when you want them to sign in and help manage the team.">
                <Button variant="contained" startIcon={<AddIcon />} size="small" sx={{ px: 2, fontWeight: 700 }} onClick={openAccountDialog}>
                  Create Account
                </Button>
              </Tooltip>
              <Tooltip title="Edit the team profile people see on invites, billing, and shared workspace pages.">
                <IconButton aria-label="Open team settings" size="small" sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }} onClick={openTeamInfoDialog}>
                  <MoreHorizIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : undefined
        }
      />

      {setupLinks.length > 0 && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => setSetupLinks([])}>Dismiss</Button>}
        >
          <Stack spacing={1}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>Registration setup links</Typography>
            {setupLinks.map((link) => (
              <Box key={`${link.userId}-${link.email}`} sx={{ display: 'flex', gap: 1.25, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{link.email}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Expires {new Date(link.expiresAt).toLocaleDateString()}</Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={() => copySetupLink(link)}>Copy link</Button>
              </Box>
            ))}
          </Stack>
        </Alert>
      )}

      {renderOverview()}

      {listDrawerOpen && canManageTeamAccounts ? (
        <Drawer
        anchor="right"
        open
        onClose={closeListDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 620, lg: 660 },
            maxWidth: '100vw',
            bgcolor: isDark ? 'background.default' : '#FFFFFF',
            backgroundColor: isDark ? undefined : '#FFFFFF',
            backgroundImage: 'none',
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box
            sx={{
              flexShrink: 0,
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: { xs: 1.5, sm: 2 },
              py: 1.25,
              bgcolor: isDark ? 'background.default' : '#FFFFFF',
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '1.02rem', fontWeight: 950, lineHeight: 1.2 }}>Invitations</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {pendingInvitations.length} pending invitation{pendingInvitations.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexShrink: 0 }}>
                {canManageTeamAccounts ? (
                  <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAccountDialog}>
                    Create Account
                  </Button>
                ) : null}
                <IconButton
                  aria-label="Close drawer"
                  size="small"
                  onClick={closeListDrawer}
                  sx={{ width: 32, height: 32, color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 0.8 }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {renderInvitations()}
          </Box>
        </Box>
        </Drawer>
      ) : null}

      {driverDrawerOpen && selectedDriverId ? (
        <Drawer
        anchor="right"
        open
        onClose={closeDriverDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 520, lg: 560 },
            maxWidth: '100vw',
            bgcolor: 'background.default',
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider', px: { xs: 1.1, sm: 1.4 }, py: 1.05 }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1.2, minHeight: 40 }}>
              <Typography sx={{ fontSize: '1.02rem', fontWeight: 950, lineHeight: 1.2 }}>Driver Details</Typography>
              <IconButton
                aria-label="Close drawer"
                size="small"
                onClick={closeDriverDrawer}
                sx={{ width: 32, height: 32, color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 0.8 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1.5, sm: 2 }, py: 2 }}>
            {selectedDriverId ? (
              <DriverDetailContent
                driverId={selectedDriverId}
                onReleased={() => {
                  void mutateDriverRoster();
                  closeDriverDrawer();
                  setSnackbarMessage('Driver released from team roster.');
                }}
              />
            ) : null}
          </Box>
        </Box>
        </Drawer>
      ) : null}

      <TeamInfoDialog
        open={teamInfoOpen}
        team={displayedTeam}
        saving={teamInfoSaving}
        error={teamInfoError}
        onClose={() => setTeamInfoOpen(false)}
        onSubmit={handleSaveTeamInfo}
      />

      <AddDriverDialog
        open={accountDialogOpen}
        onClose={() => setAccountDialogOpen(false)}
        onSubmit={handleCreateDriverAccount}
        onSubmitMember={handleCreateTeamAccount}
        showMemberType
        isFamilyTeamWorkspace={Boolean(teamProfile?.isFamilyTeamWorkspace)}
      />

      <AddDriverDialog
        open={addDriverDrawerOpen}
        onClose={() => setAddDriverDrawerOpen(false)}
        onSubmit={handleCreateDriverAccount}
      />

      <Dialog open={Boolean(rolesDialogMember)} onClose={closeMemberRolesDialog} fullWidth maxWidth="xs">
        <DialogTitle>Member roles</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.86rem', fontWeight: 800 }}>{rolesDialogMember?.name}</Typography>
              <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>{rolesDialogMember?.email}</Typography>
            </Box>
            <FormGroup>
              {EDITABLE_MEMBER_ROLE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.role}
                  control={(
                    <Checkbox
                      checked={memberRoleSelections.includes(option.role)}
                      onChange={(event) => toggleMemberRole(option.role, event.target.checked)}
                    />
                  )}
                  label={option.label}
                />
              ))}
            </FormGroup>
            {memberRolesError ? <Alert severity="error">{memberRolesError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMemberRolesDialog} disabled={memberRolesSaving}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleSaveMemberRoles()} disabled={memberRolesSaving}>
            {memberRolesSaving ? 'Saving…' : 'Save roles'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={3500}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
